import { useState, useEffect, useRef } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Loader2, Plus, ArrowLeft, Sparkles, Save, Eye, EyeOff, Trash2, Globe, FileText, X, Tag, RefreshCw, Upload, Image } from "lucide-react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";

export default function OpsInsights() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [genTopic, setGenTopic] = useState("");
  const [genKeyword, setGenKeyword] = useState("");
  const [refineFeedback, setRefineFeedback] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkTitles, setBulkTitles] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkActing, setBulkActing] = useState(false);
  const [form, setForm] = useState({ title: "", subtitle: "", slug: "", body: "", tags: [], status: "draft", featured_image: "", published_at: "", seo: { meta_title: "", meta_description: "", keywords: [] } });
  const [tagInput, setTagInput] = useState("");
  const [kwInput, setKwInput] = useState("");
  const fileRef = useRef(null);

  const loadArticles = async () => {
    try {
      const param = filter !== "all" ? `?status=${filter}` : "";
      const { data } = await api.get(`/insights/admin/articles${param}`);
      setArticles(data);
    } catch (e) { console.error("Articles load failed:", e); }
    setLoading(false);
  };

  useEffect(() => { loadArticles(); }, [filter]);

  const startNew = () => {
    setForm({ title: "", subtitle: "", slug: "", body: "", tags: [], status: "draft", featured_image: "", published_at: "", seo: { meta_title: "", meta_description: "", keywords: [] } });
    setEditing("new");
    setRefineFeedback("");
  };

  const startEdit = async (id) => {
    try {
      const { data } = await api.get(`/insights/admin/articles/${id}`);
      setForm({
        title: data.title || "", subtitle: data.subtitle || "", slug: data.slug || "",
        body: data.body || "", tags: data.tags || [], status: data.status || "draft",
        featured_image: data.featured_image || "", published_at: data.published_at || "",
        seo: { meta_title: data.seo?.meta_title || "", meta_description: data.seo?.meta_description || "", keywords: data.seo?.keywords || [] },
      });
      setEditing(data);
      setRefineFeedback("");
    } catch (e) { console.error("Load article failed:", e); }
  };

  const generateContent = async () => {
    if (!genTopic.trim()) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/insights/admin/generate", { topic: genTopic, keyword: genKeyword });
      setForm(prev => ({
        ...prev,
        title: data.title || prev.title,
        subtitle: data.subtitle || prev.subtitle,
        body: data.body || prev.body,
        tags: data.tags || prev.tags,
        seo: {
          meta_title: data.seo?.meta_title || prev.seo.meta_title,
          meta_description: data.seo?.meta_description || prev.seo.meta_description,
          keywords: data.seo?.keywords || prev.seo.keywords,
        },
      }));
      setGenTopic(""); setGenKeyword("");
    } catch (e) { console.error("Generate failed:", e); }
    finally { setGenerating(false); }
  };

  const refineContent = async () => {
    if (!refineFeedback.trim() || !form.body) return;
    setRefining(true);
    try {
      const { data } = await api.post("/insights/admin/refine", { title: form.title, subtitle: form.subtitle, body: form.body, feedback: refineFeedback });
      setForm(prev => {
        const updated = { ...prev };
        if (data.body) updated.body = data.body;
        if (data.title) updated.title = data.title;
        if (data.subtitle) updated.subtitle = data.subtitle;
        if (data.tags) updated.tags = data.tags;
        if (data.seo) {
          updated.seo = {
            meta_title: data.seo.meta_title || prev.seo.meta_title,
            meta_description: data.seo.meta_description || prev.seo.meta_description,
            keywords: data.seo.keywords || prev.seo.keywords,
          };
        }
        return updated;
      });
      setRefineFeedback("");
    } catch (e) { console.error("Refine failed:", e); }
    finally { setRefining(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/insights/admin/upload-image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(prev => ({ ...prev, featured_image: data.url }));
    } catch (e) { console.error("Upload failed:", e); alert("Image upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveArticle = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/insights/admin/articles", form);
      } else {
        await api.put(`/insights/admin/articles/${editing.id}`, form);
      }
      setEditing(null);
      loadArticles();
    } catch (e) { console.error("Save failed:", e); }
    finally { setSaving(false); }
  };

  const publishArticle = async () => {
    setSaving(true);
    try {
      const payload = { ...form, status: "published" };
      if (editing === "new") {
        await api.post("/insights/admin/articles", payload);
      } else {
        await api.put(`/insights/admin/articles/${editing.id}`, payload);
      }
      setEditing(null);
      loadArticles();
    } catch (e) { console.error("Publish failed:", e); }
    finally { setSaving(false); }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    try {
      await api.delete(`/insights/admin/articles/${id}`);
      if (editing?.id === id) setEditing(null);
      loadArticles();
    } catch (e) { console.error("Delete failed:", e); }
  };

  const toggleSelect = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleSelectAll = () => setSelected(prev => prev.size === articles.length ? new Set() : new Set(articles.map(a => a.id)));
  const bulkAction = async (action) => {
    if (selected.size === 0) return;
    const label = action === "delete" ? `Delete ${selected.size} article(s)? This cannot be undone.` : `${action === "publish" ? "Publish" : "Unpublish"} ${selected.size} article(s)?`;
    if (!window.confirm(label)) return;
    setBulkActing(true);
    try {
      await api.post("/insights/admin/bulk-action", { ids: Array.from(selected), action });
      setSelected(new Set());
      loadArticles();
    } catch (e) { console.error("Bulk action failed:", e); }
    finally { setBulkActing(false); }
  };

  const startBulkGenerate = async () => {
    const lines = bulkTitles.split("\n").map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) return;
    setBulkRunning(true);
    setBulkProgress({ total: lines.length, completed: 0, failed: 0, results: [], status: "processing" });
    try {
      const { data } = await api.post("/insights/admin/bulk-generate", { titles: lines });
      const batchId = data.batch_id;
      // Poll for progress
      const poll = setInterval(async () => {
        try {
          const { data: status } = await api.get(`/insights/admin/bulk-status/${batchId}`);
          setBulkProgress(status);
          if (status.status === "done") {
            clearInterval(poll);
            setBulkRunning(false);
            loadArticles();
          }
        } catch (e) { console.error("Poll failed:", e); }
      }, 3000);
    } catch (e) {
      console.error("Bulk generate failed:", e);
      setBulkRunning(false);
    }
  };

  const addTag = () => { if (!tagInput.trim() || form.tags.includes(tagInput.trim())) return; setForm(p => ({ ...p, tags: [...p.tags, tagInput.trim()] })); setTagInput(""); };
  const removeTag = (t) => setForm(p => ({ ...p, tags: p.tags.filter(x => x !== t) }));
  const addKeyword = () => { if (!kwInput.trim() || form.seo.keywords.includes(kwInput.trim())) return; setForm(p => ({ ...p, seo: { ...p.seo, keywords: [...p.seo.keywords, kwInput.trim()] } })); setKwInput(""); };
  const removeKeyword = (k) => setForm(p => ({ ...p, seo: { ...p.seo, keywords: p.seo.keywords.filter(x => x !== k) } }));

  // ─── EDITOR ───
  if (editing) {
    return (
      <div data-testid="insights-editor">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setEditing(null)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0033CC] font-['Outfit'] transition-colors" data-testid="back-to-list">
            <ArrowLeft className="w-4 h-4" />Back to Articles
          </button>
          <div className="flex gap-2">
            <Button onClick={() => setPreviewing(!previewing)} variant="outline" className="rounded-none h-9 px-4 text-sm font-['Outfit']" data-testid="preview-toggle-btn">
              {previewing ? <><EyeOff className="w-4 h-4 mr-1.5" />Edit</> : <><Eye className="w-4 h-4 mr-1.5" />Preview</>}
            </Button>
            <Button onClick={saveArticle} disabled={saving || !form.title.trim()} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-9 px-5 text-sm font-['Outfit']" data-testid="save-article-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}Save Draft
            </Button>
            {form.status !== "published" && (
              <Button onClick={publishArticle} disabled={saving || !form.title.trim() || !form.body.trim()} className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-none h-9 px-5 text-sm font-['Outfit']" data-testid="publish-btn">
                <Globe className="w-4 h-4 mr-1.5" />Publish
              </Button>
            )}
            {form.status === "published" && (
              <Button onClick={() => setForm(p => ({ ...p, status: "draft" }))} variant="outline" className="rounded-none h-9 text-sm font-['Outfit']" data-testid="unpublish-btn">Unpublish</Button>
            )}
          </div>
        </div>

        {/* AI Generator */}
        <div className="bg-[#0F172A] p-5 mb-4" data-testid="ai-generator">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#818CF8]" />
            <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#818CF8]">AI Content Engine</span>
          </div>
          <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] mb-3">Enter a topic and keyword to generate a full article with Bain/McKinsey-style insight and persuasive copywriting.</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Input value={genTopic} onChange={e => setGenTopic(e.target.value)} placeholder="Topic (e.g. How AI agents replace manual sales outreach)" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-none h-9 text-sm" data-testid="gen-topic" />
            <Input value={genKeyword} onChange={e => setGenKeyword(e.target.value)} placeholder="Target keyword (e.g. AI sales automation)" className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-none h-9 text-sm" data-testid="gen-keyword" />
          </div>
          <Button onClick={generateContent} disabled={generating || !genTopic.trim()} className="bg-[#818CF8] text-white hover:bg-[#6366F1] rounded-none h-8 px-4 text-xs font-['Outfit']" data-testid="generate-btn">
            {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate Article</>}
          </Button>
        </div>

        {/* AI Refine / Re-spin */}
        {form.body && (
          <div className="bg-slate-50 border border-slate-200 p-4 mb-4" data-testid="ai-refine">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="w-3.5 h-3.5 text-[#0033CC]" />
              <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#0033CC]">Refine with AI</span>
            </div>
            <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-2">Give feedback to re-spin the article. E.g. "Make the opening more provocative" or "Add a section about ROI math".</p>
            <div className="flex gap-2">
              <Textarea value={refineFeedback} onChange={e => setRefineFeedback(e.target.value)} placeholder="Your feedback or direction for the AI..." className="flex-1 rounded-none border-slate-300 text-sm min-h-[50px] resize-none" data-testid="refine-input"
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); refineContent(); } }} />
              <Button onClick={refineContent} disabled={refining || !refineFeedback.trim()} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-auto px-4 text-xs font-['Outfit'] self-end" data-testid="refine-btn">
                {refining ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Re-spin</>}
              </Button>
            </div>
          </div>
        )}

        {previewing ? (
          /* ─── PREVIEW MODE ─── */
          <div className="bg-white border border-slate-200" data-testid="article-preview">
            <div className="border-b border-slate-200 bg-amber-50 px-6 py-2 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-wider text-amber-700">Draft Preview</span>
              <span className="text-xs text-amber-600 font-['IBM_Plex_Sans'] ml-auto">This is how the article will appear to readers</span>
            </div>
            <div className="max-w-3xl mx-auto px-8 py-10">
              {/* Tags */}
              {form.tags.length > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  {form.tags.map(tag => <span key={tag} className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#0033CC] bg-[#0033CC]/5 px-2 py-0.5">{tag}</span>)}
                </div>
              )}
              {/* Title */}
              <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-medium text-[#0F172A] tracking-tight leading-[1.15] mb-4" data-testid="preview-title">{form.title || "Untitled Article"}</h1>
              {/* Subtitle */}
              {form.subtitle && <p className="text-lg text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed mb-6">{form.subtitle}</p>}
              {/* Meta */}
              <div className="flex items-center gap-4 text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-8 pb-8 border-b border-slate-200">
                <span>Stansbury AI</span>
                <span>{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
              </div>
              {/* Featured Image */}
              {form.featured_image && <img src={form.featured_image} alt="" className="w-full h-64 object-cover mb-8 border border-slate-200" />}
              {/* Inline CTA */}
              <div className="bg-[#0033CC]/5 border border-[#0033CC]/10 p-5 mb-8">
                <p className="text-sm font-['IBM_Plex_Sans'] text-[#0F172A]">
                  <strong className="font-['Outfit']">Want to know your AI readiness score?</strong> Get a free diagnostic across 6 growth dimensions.
                  <span className="text-[#0033CC] font-medium ml-1">Start your Blueprint &rarr;</span>
                </p>
              </div>
              {/* Body */}
              <div data-testid="preview-body">
                <ReactMarkdown
                  components={{
                    h2: ({ children }) => <h2 className="text-xl font-['Outfit'] font-medium text-[#0F172A] mt-10 mb-4 tracking-tight">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-lg font-['Outfit'] font-medium text-[#0F172A] mt-8 mb-3">{children}</h3>,
                    p: ({ children }) => <p className="text-[15px] text-slate-600 font-['IBM_Plex_Sans'] leading-[1.8] mb-4">{children}</p>,
                    ul: ({ children }) => <ul className="space-y-2 mb-6 ml-4">{children}</ul>,
                    ol: ({ children }) => <ol className="space-y-2 mb-6 ml-4 list-decimal">{children}</ol>,
                    li: ({ children }) => <li className="text-[15px] text-slate-600 font-['IBM_Plex_Sans'] leading-[1.7] pl-1">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold text-[#0F172A]">{children}</strong>,
                    blockquote: ({ children }) => <blockquote className="border-l-[3px] border-[#0033CC] pl-4 py-2 my-6 bg-slate-50">{children}</blockquote>,
                    a: ({ href, children }) => <a href={href} className="text-[#0033CC] font-medium hover:underline">{children}</a>,
                  }}
                >{form.body || "*No content yet*"}</ReactMarkdown>
              </div>
              {/* End CTA */}
              <div className="mt-12 bg-[#0F172A] p-8 md:p-10">
                <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#818CF8] mb-3">Your Next Move</p>
                <h2 className="text-2xl font-['Outfit'] font-medium text-white mb-3">Get Your AI Transformation Blueprint</h2>
                <p className="text-sm text-slate-400 font-['IBM_Plex_Sans'] leading-relaxed mb-6 max-w-lg">
                  Discover exactly where AI can drive revenue, cut costs, and build competitive advantage for your business.
                </p>
                <span className="inline-flex items-center gap-2 bg-[#0033CC] text-white px-8 py-3 font-['Outfit'] font-semibold text-sm">
                  Begin Your Blueprint <span>&rarr;</span>
                </span>
              </div>
              {/* SEO Preview */}
              {(form.seo.meta_title || form.seo.meta_description) && (
                <div className="mt-8 border border-slate-200 p-4 bg-slate-50">
                  <p className="text-xs font-['JetBrains_Mono'] uppercase tracking-wider text-slate-400 mb-2">Google Search Preview</p>
                  <p className="text-[#1a0dab] text-base font-medium leading-tight mb-1" style={{ fontFamily: "Arial, sans-serif" }}>{form.seo.meta_title || form.title}</p>
                  <p className="text-[#006621] text-xs mb-1" style={{ fontFamily: "Arial, sans-serif" }}>stansbury.ai/insights/{form.slug || "..."}</p>
                  <p className="text-xs text-[#545454] leading-relaxed" style={{ fontFamily: "Arial, sans-serif" }}>{form.seo.meta_description || form.subtitle}</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ─── EDIT MODE ─── */
        <div className="grid grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="col-span-2 space-y-4">
            <div>
              <Label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Title</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="rounded-none border-slate-300 h-10 text-base font-['Outfit'] font-medium" data-testid="article-title-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Subtitle</Label>
              <Input value={form.subtitle} onChange={e => setForm(p => ({ ...p, subtitle: e.target.value }))} className="rounded-none border-slate-300 h-9 text-sm" data-testid="article-subtitle-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Body (Markdown)</Label>
              <Textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} className="rounded-none border-slate-300 text-sm min-h-[400px] font-mono leading-relaxed" data-testid="article-body-input" />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Status */}
            <div className="bg-white border border-slate-200 p-4">
              <Label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-2 block">Status</Label>
              <div className={`inline-block text-xs font-['JetBrains_Mono'] px-2 py-1 ${form.status === "published" ? "bg-emerald-50 text-emerald-700" : form.published_at && new Date(form.published_at) > new Date() ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                {form.status === "published" && form.published_at && new Date(form.published_at) > new Date() ? "scheduled" : form.status}
              </div>
            </div>

            {/* Publish Date */}
            <div className="bg-white border border-slate-200 p-4" data-testid="publish-date-section">
              <Label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-2 block">Publish Date</Label>
              <p className="text-[10px] text-slate-400 font-['IBM_Plex_Sans'] mb-2">Backdate or schedule ahead. Leave blank to publish immediately.</p>
              <Input
                type="datetime-local"
                value={form.published_at ? form.published_at.slice(0, 16) : ""}
                onChange={e => {
                  const val = e.target.value;
                  setForm(p => ({ ...p, published_at: val ? new Date(val).toISOString() : "" }));
                }}
                className="rounded-none border-slate-300 h-8 text-xs font-mono"
                data-testid="publish-date-input" />
              {form.published_at && (
                <div className="mt-2 flex items-center justify-between">
                  <span className={`text-[10px] font-['JetBrains_Mono'] ${new Date(form.published_at) > new Date() ? "text-blue-600" : "text-slate-500"}`}>
                    {new Date(form.published_at) > new Date() ? "Scheduled for future" : "Backdated"}
                  </span>
                  <button onClick={() => setForm(p => ({ ...p, published_at: "" }))} className="text-[10px] text-slate-400 hover:text-red-500 font-['IBM_Plex_Sans']">Clear</button>
                </div>
              )}
            </div>

            {/* Featured Image */}
            <div className="bg-white border border-slate-200 p-4" data-testid="image-upload-section">
              <Label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-2 block">Featured Image</Label>
              {form.featured_image ? (
                <div className="relative mb-2">
                  <img src={form.featured_image} alt="Featured" className="w-full h-32 object-cover border border-slate-200" />
                  <button onClick={() => setForm(p => ({ ...p, featured_image: "" }))} className="absolute top-1 right-1 bg-white/80 p-1 hover:bg-red-50"><X className="w-3 h-3 text-red-500" /></button>
                </div>
              ) : (
                <div className="h-24 border-2 border-dashed border-slate-200 flex items-center justify-center mb-2">
                  <Image className="w-6 h-6 text-slate-300" />
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImageUpload} className="hidden" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="w-full rounded-none h-8 text-xs font-['Outfit']" data-testid="upload-image-btn">
                {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Upload className="w-3 h-3 mr-1.5" />}
                {uploading ? "Uploading..." : "Upload Image"}
              </Button>
              <p className="text-[10px] text-slate-400 mt-1 text-center">JPEG, PNG, WebP, GIF. Max 5MB.</p>
            </div>

            {/* Slug */}
            <div className="bg-white border border-slate-200 p-4">
              <Label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-1 block">Slug</Label>
              <Input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} placeholder="auto-generated" className="rounded-none border-slate-300 h-8 text-xs font-mono" data-testid="article-slug-input" />
            </div>

            {/* Tags */}
            <div className="bg-white border border-slate-200 p-4" data-testid="tag-manager">
              <Label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-2 block">Tags</Label>
              <div className="flex flex-wrap gap-1 mb-2">
                {form.tags.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 text-[10px] font-['JetBrains_Mono'] bg-[#0033CC]/5 text-[#0033CC] px-2 py-0.5">
                    {t}<button onClick={() => removeTag(t)} className="hover:text-red-500"><X className="w-2.5 h-2.5" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Add tag" className="rounded-none border-slate-300 h-7 text-xs flex-1" onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())} data-testid="tag-input" />
                <Button onClick={addTag} size="sm" variant="outline" className="rounded-none h-7 text-xs px-2"><Plus className="w-3 h-3" /></Button>
              </div>
            </div>

            {/* SEO */}
            <div className="bg-white border border-slate-200 p-4" data-testid="seo-panel">
              <Label className="text-xs text-slate-400 font-['JetBrains_Mono'] uppercase mb-2 block">SEO</Label>
              <div className="space-y-2">
                <Input value={form.seo.meta_title} onChange={e => setForm(p => ({ ...p, seo: { ...p.seo, meta_title: e.target.value } }))} placeholder="Meta title" className="rounded-none border-slate-300 h-8 text-xs" data-testid="seo-title" />
                <Textarea value={form.seo.meta_description} onChange={e => setForm(p => ({ ...p, seo: { ...p.seo, meta_description: e.target.value } }))} placeholder="Meta description" className="rounded-none border-slate-300 text-xs min-h-[50px]" data-testid="seo-desc" />
                <div className="flex flex-wrap gap-1 mb-1">
                  {form.seo.keywords.map(k => (
                    <span key={k} className="inline-flex items-center gap-1 text-[10px] font-['JetBrains_Mono'] bg-slate-100 px-2 py-0.5">
                      {k}<button onClick={() => removeKeyword(k)} className="hover:text-red-500"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <Input value={kwInput} onChange={e => setKwInput(e.target.value)} placeholder="Add keyword" className="rounded-none border-slate-300 h-7 text-xs flex-1" onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addKeyword())} data-testid="seo-kw-input" />
                  <Button onClick={addKeyword} size="sm" variant="outline" className="rounded-none h-7 text-xs px-2"><Plus className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>

            {/* Delete */}
            {editing !== "new" && (
              <Button onClick={() => deleteArticle(editing.id)} variant="outline" className="w-full rounded-none h-8 text-xs border-red-200 text-red-600 hover:bg-red-50 font-['Outfit']" data-testid="delete-article-btn">
                <Trash2 className="w-3 h-3 mr-1.5" />Delete Article
              </Button>
            )}
          </div>
        </div>
        )}
      </div>
    );
  }

  // ─── LIST VIEW ───
  return (
    <div data-testid="ops-insights">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-['Outfit'] font-medium text-[#0F172A]">Insights</h1>
          <p className="text-sm text-slate-500 font-['IBM_Plex_Sans'] mt-1">Manage your content engine. {articles.length} article{articles.length !== 1 ? "s" : ""}.</p>
        </div>
        <div className="flex gap-2">
          <div className="bg-white border border-slate-200 p-0.5 flex">
            {["all", "draft", "published"].map(s => (
              <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1 text-xs font-['Outfit'] transition-colors capitalize ${filter === s ? "bg-[#0033CC] text-white" : "text-slate-500 hover:text-slate-700"}`} data-testid={`filter-${s}`}>{s}</button>
            ))}
          </div>
          <Button onClick={startNew} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-9 px-5 text-sm font-['Outfit']" data-testid="new-article-btn">
            <Plus className="w-4 h-4 mr-1.5" />New Article
          </Button>
          <Button onClick={() => { setShowBulk(true); setBulkProgress(null); setBulkRunning(false); }} variant="outline" className="rounded-none h-9 px-4 text-sm font-['Outfit'] border-[#0033CC] text-[#0033CC]" data-testid="bulk-create-btn">
            <Sparkles className="w-4 h-4 mr-1.5" />Bulk Create
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#0033CC]" /></div>
      ) : articles.length === 0 ? (
        <div className="text-center py-20 bg-white border border-slate-200">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-lg font-['Outfit'] text-slate-400 mb-2">No articles yet</p>
          <p className="text-sm text-slate-400 font-['IBM_Plex_Sans'] mb-4">Create your first article to start building your content engine.</p>
          <Button onClick={startNew} className="bg-[#0033CC] text-white rounded-none font-['Outfit']"><Plus className="w-4 h-4 mr-1.5" />Create Article</Button>
        </div>
      ) : (
        <>
          {/* Bulk Action Bar */}
          {selected.size > 0 && (
            <div className="bg-[#0F172A] p-3 mb-3 flex items-center justify-between" data-testid="bulk-action-bar">
              <span className="text-xs text-white font-['Outfit']">{selected.size} selected</span>
              <div className="flex gap-2">
                <Button onClick={() => bulkAction("publish")} disabled={bulkActing} size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-none h-7 text-xs font-['Outfit']" data-testid="bulk-publish-btn">
                  <Globe className="w-3 h-3 mr-1" />Publish
                </Button>
                <Button onClick={() => bulkAction("unpublish")} disabled={bulkActing} size="sm" variant="outline" className="rounded-none h-7 text-xs font-['Outfit'] border-white/20 text-white hover:bg-white/10" data-testid="bulk-unpublish-btn">
                  Unpublish
                </Button>
                <Button onClick={() => bulkAction("delete")} disabled={bulkActing} size="sm" className="bg-red-600 text-white hover:bg-red-700 rounded-none h-7 text-xs font-['Outfit']" data-testid="bulk-delete-btn">
                  <Trash2 className="w-3 h-3 mr-1" />Delete
                </Button>
                <button onClick={() => setSelected(new Set())} className="text-xs text-white/50 hover:text-white ml-2 font-['Outfit']">Clear</button>
              </div>
            </div>
          )}
          {/* Select All */}
          <div className="flex items-center gap-2 mb-2">
            <button onClick={toggleSelectAll} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#0033CC] font-['Outfit'] transition-colors" data-testid="select-all-btn">
              <div className={`w-4 h-4 border flex items-center justify-center ${selected.size === articles.length && articles.length > 0 ? "bg-[#0033CC] border-[#0033CC]" : "border-slate-300"}`}>
                {selected.size === articles.length && articles.length > 0 && <span className="text-white text-[10px]">&#10003;</span>}
              </div>
              Select all
            </button>
          </div>
          <div className="space-y-2">
            {articles.map((a, i) => (
              <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className={`bg-white border p-4 flex items-center gap-4 cursor-pointer transition-colors ${selected.has(a.id) ? "border-[#0033CC] bg-[#0033CC]/[0.02]" : "border-slate-200 hover:border-[#0033CC]/20"}`}
                data-testid={`article-row-${i}`}>
                <button onClick={(e) => { e.stopPropagation(); toggleSelect(a.id); }}
                  className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 ${selected.has(a.id) ? "bg-[#0033CC] border-[#0033CC]" : "border-slate-300 hover:border-[#0033CC]"}`}
                  data-testid={`select-${a.id}`}>
                  {selected.has(a.id) && <span className="text-white text-xs">&#10003;</span>}
                </button>
                <div className="flex-1 min-w-0" onClick={() => startEdit(a.id)}>
                  {a.featured_image && <img src={a.featured_image} alt="" className="w-16 h-12 object-cover border border-slate-200 float-left mr-3" />}
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] truncate">{a.title || "Untitled"}</h3>
                    <span className={`text-[10px] font-['JetBrains_Mono'] px-1.5 py-0.5 flex-shrink-0 ${a.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{a.status}</span>
                  </div>
                  <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] truncate">{a.subtitle || a.slug}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {a.tags?.slice(0, 2).map(t => <span key={t} className="text-[10px] font-['JetBrains_Mono'] bg-slate-100 text-slate-500 px-1.5 py-0.5">{t}</span>)}
                  {a.published_at && new Date(a.published_at) > new Date() && <span className="text-[10px] font-['JetBrains_Mono'] bg-blue-50 text-blue-700 px-1.5 py-0.5">scheduled</span>}
                  <span className="text-xs text-slate-400 font-['IBM_Plex_Sans']">{(a.published_at || a.created_at) ? new Date(a.published_at || a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}

      {/* Bulk Create Modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !bulkRunning && setShowBulk(false)}>
          <div className="bg-white border border-slate-200 w-full max-w-2xl" onClick={e => e.stopPropagation()} data-testid="bulk-create-modal">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#0033CC]" />
                <div>
                  <h3 className="text-base font-['Outfit'] font-medium text-[#0F172A]">Bulk Create Articles</h3>
                  <p className="text-xs text-slate-400 font-['IBM_Plex_Sans']">One title per line. AI generates the full article for each.</p>
                </div>
              </div>
              {!bulkRunning && <button onClick={() => setShowBulk(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>}
            </div>
            <div className="p-5">
              {!bulkProgress || bulkProgress.status !== "processing" ? (
                <>
                  <Textarea
                    value={bulkTitles}
                    onChange={e => setBulkTitles(e.target.value)}
                    placeholder={"How AI agents are replacing cold outreach\nThe 5 revenue leaks costing SMBs $500K/year\nWhy your sales pipeline needs agentic AI\nThe founder's guide to AI implementation"}
                    className="rounded-none border-slate-300 text-sm min-h-[180px] font-mono mb-3"
                    disabled={bulkRunning}
                    data-testid="bulk-titles-input" />
                  <p className="text-xs text-slate-400 font-['IBM_Plex_Sans'] mb-4">
                    {bulkTitles.split("\n").filter(l => l.trim()).length} article{bulkTitles.split("\n").filter(l => l.trim()).length !== 1 ? "s" : ""} will be generated as drafts. Max 20 per batch.
                  </p>
                </>
              ) : (
                <div className="space-y-3" data-testid="bulk-progress">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-['Outfit'] font-medium text-[#0F172A]">Generating articles...</span>
                    <span className="text-xs font-['JetBrains_Mono'] text-[#0033CC]">{bulkProgress.completed + bulkProgress.failed}/{bulkProgress.total}</span>
                  </div>
                  <div className="h-2 bg-slate-100 overflow-hidden">
                    <div className="h-full bg-[#0033CC] transition-all duration-500" style={{ width: `${((bulkProgress.completed + bulkProgress.failed) / bulkProgress.total) * 100}%` }} />
                  </div>
                  <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                    {(bulkProgress.results || []).map((r, i) => (
                      <div key={`br-${i}`} className={`flex items-center gap-2 p-2 text-xs ${r.status === "success" ? "bg-emerald-50" : "bg-red-50"}`}>
                        <span className={`font-['JetBrains_Mono'] ${r.status === "success" ? "text-emerald-600" : "text-red-600"}`}>{r.status === "success" ? "OK" : "ERR"}</span>
                        <span className="text-slate-700 font-['IBM_Plex_Sans'] truncate">{r.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              {bulkProgress?.status === "done" ? (
                <Button onClick={() => { setShowBulk(false); setBulkProgress(null); setBulkTitles(""); }} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-9 px-5 text-sm font-['Outfit']" data-testid="bulk-done-btn">
                  Done — {bulkProgress.completed} article{bulkProgress.completed !== 1 ? "s" : ""} created
                </Button>
              ) : (
                <>
                  <Button onClick={() => { setShowBulk(false); setBulkRunning(false); setBulkProgress(null); }} variant="outline" className="rounded-none h-9 text-sm font-['Outfit']">Cancel</Button>
                  <Button onClick={startBulkGenerate} disabled={bulkRunning || !bulkTitles.trim()} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-9 px-5 text-sm font-['Outfit']" data-testid="bulk-start-btn">
                    {bulkRunning ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Generating...</> : <><Sparkles className="w-4 h-4 mr-1.5" />Generate All</>}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
